export const ARTICLES_EN = [
  {
    id: "que-es-agente-ia",
    slug: "what-is-an-ai-agent-for-business",
    title: "What Is an AI Agent for Business? (And Why Yours Probably Needs One)",
    desc: "What an AI agent actually is, how it differs from a chatbot, and how businesses use them to sell and support around the clock.",
    minutes: 5,
    date: "2026-07-02",
    wa: "ai",
    ctaTitle: "Want to see what an AI agent would look like in your business?",
    sections: [
      {
        h: "The simple definition",
        p: ["An AI agent is a system that understands natural language, reasons about context and takes action. Unlike a traditional chatbot — which follows a tree of pre-written answers — an agent can query your catalog, book a meeting, log a lead in your CRM or draft a quote, all within the same conversation."],
      },
      {
        h: "AI agent vs. traditional chatbot",
        p: ["The classic chatbot frustrates: rigid buttons, generic answers and the dreaded 'I didn't understand that.' An AI agent understands poorly typed questions, remembers conversation context and has access to tools: your database, your calendar, your documents."],
        list: ["Chatbot: follows fixed flows, breaks on the unexpected", "AI agent: understands intent, uses your data and takes action", "Agent with handoff: escalates to humans when the case requires it"],
      },
      {
        h: "Real business use cases",
        p: ["In sales: qualifies leads and books meetings while your team sleeps. In support: resolves 60-80% of frequent questions instantly. In operations: checks order status, stock or billing with zero human intervention."],
      },
      {
        h: "What you need to start",
        p: ["Less than you think: your sales documents, one channel (web or WhatsApp) and a clear use case. A first working agent takes 2 to 4 weeks. The key is starting small — one process, one channel — and scaling with measured results."],
      },
    ],
    faqs: [
      { q: "Does an AI agent replace people?", a: "It replaces tasks, not people: repetitive questions, data entry and follow-up. Your team stays for the cases that genuinely need human judgment." },
      { q: "How much does it cost to implement one?", a: "Our DEVOLUZION AI plan starts at USD 299 and includes assessment, a working agent, integration and training." },
    ],
  },
  {
    id: "implementar-ia",
    slug: "how-to-implement-ai-in-your-business",
    title: "How to Implement AI in Your Business (Without Never-Ending Projects)",
    desc: "A practical guide to implementing AI in a company: assessment, scoped pilot, measurement and scale.",
    minutes: 6,
    date: "2026-07-05",
    wa: "ai",
    ctaTitle: "Book an AI assessment for your business",
    sections: [
      {
        h: "The most common mistake",
        p: ["Most AI projects fail by starting too big: 'transforming the company with AI.' The ones that work start ridiculously concrete: automating the first reply to leads, summarizing meeting notes, classifying support emails."],
      },
      {
        h: "Step 1: Opportunity assessment",
        p: ["Before talking technology, map where hours are being lost. Ask every area: what repetitive task do you hate? What question do you answer 20 times a day? Those are your first use cases. Prioritize by impact (hours saved or revenue generated) and effort (available data, required integrations)."],
      },
      {
        h: "Step 2: The 30-day pilot",
        p: ["Pick ONE use case and run it as a scoped pilot: one channel, one process, metrics defined before starting. A well-scoped pilot costs little and answers the only question that matters: does this return value to my business?"],
      },
      {
        h: "Step 3: Measure and scale",
        p: ["Define the baseline before the pilot (response time, weekly hours, conversion rate) and compare after. If the pilot works, scale to more channels or processes. If it doesn't, you learned cheaply and the next attempt will be better."],
      },
      {
        h: "The human factor",
        p: ["AI that isn't adopted is useless. Train your team, name an internal owner and celebrate the first visible wins. People drive the transformation; AI just accelerates it."],
      },
    ],
    faqs: [
      { q: "Do I need a tech department to implement AI?", a: "No. You need a technical partner and an internal owner who knows the business. We bring the technology; you bring the process knowledge." },
      { q: "How much does an AI pilot cost?", a: "A scoped pilot starts around USD 299 with our DEVOLUZION AI plan, including assessment and deployment." },
    ],
  },
  {
    id: "costo-web-chile",
    slug: "how-much-does-a-professional-website-cost",
    title: "How Much Does a Professional Website Cost in 2026?",
    desc: "Real website pricing: what each range includes, what separates a cheap site from a premium one, and how to avoid overpaying.",
    minutes: 6,
    date: "2026-07-08",
    wa: "web",
    ctaTitle: "Get a website quote from DEVOLUZION",
    sections: [
      {
        h: "Real market ranges",
        p: ["A website can cost anywhere from a few hundred dollars (basic template) to tens of thousands (complex platforms). The right question isn't how much it costs, but what it includes and what return it generates."],
        list: ["$200 - $500: templates with your logo. Fast, generic, no strategy", "$500 - $2,000: custom sites with design and basic SEO", "$2,000+: premium experiences, ecommerce, custom functionality"],
      },
      {
        h: "What makes a website premium",
        p: ["Three things: speed (green Core Web Vitals), conversion (structure designed to sell, not to show off) and technical SEO built into the code. A premium website isn't a luxury: it's the difference between a site that builds trust and one that destroys it in 3 seconds."],
      },
      {
        h: "The hidden costs nobody mentions",
        p: ["Hosting and domain ($50-$300/year), maintenance, plugin licenses and — the most ignored — the cost of a slow or generic website: every customer who doesn't contact you because your site didn't inspire confidence."],
      },
      {
        h: "How to evaluate a quote",
        p: ["Ask for specifics: custom design or template, technical SEO included, speed targets, who writes the content, what happens with post-launch support and who owns the code. A serious quote answers all of that without being asked twice."],
      },
      {
        h: "Our reference point",
        p: ["At DEVOLUZION, the START plan begins at USD 149 + applicable taxes and includes a premium website, UX/UI, initial technical SEO, analytics, WhatsApp and contact forms. No fine print."],
      },
    ],
    faqs: [
      { q: "Is a free or cheap website enough to start?", a: "To validate an idea, yes. For a company that sells, a generic site usually costs more in lost customers than it saves." },
      { q: "Does the price include ranking on Google?", a: "Technical SEO (structure, metadata, sitemap) should be included. Ranking for competitive keywords is additional ongoing work." },
    ],
  },
  {
    id: "costo-software",
    slug: "custom-software-development-business-guide",
    title: "Custom Software Development: What It Costs and How to Do It Right",
    desc: "The factors that drive custom software costs, reference ranges and how to avoid projects that never ship.",
    minutes: 6,
    date: "2026-07-11",
    wa: "software",
    ctaTitle: "Let's talk about the software your company needs",
    sections: [
      {
        h: "Why nobody can quote without a conversation",
        p: ["Building software is like building a house: the price depends on size, materials and finishes. A simple management system can cost a few thousand dollars; a platform with integrations, roles and reporting, considerably more. Be suspicious of anyone who quotes without asking questions."],
      },
      {
        h: "The factors that move the price",
        p: ["Number of users and roles, integrations with other systems (ERP, accounting, billing), complexity of approval flows, reports and dashboards, and security requirements. Every 'it would also be nice if...' adds up."],
      },
      {
        h: "The MVP approach: pay less to learn more",
        p: ["The smart way to start: a first version with only the essentials, in 4 to 8 weeks, with closed scope and price. You use it, learn what's actually missing and only then invest in phase two. Less risk, more learning."],
      },
      {
        h: "How to avoid the never-ending project",
        p: ["Demand: written scope by phases, visible weekly deliverables, a staging environment where you see real progress and code ownership from day one. If a provider doesn't offer those four things, it's a red flag."],
      },
    ],
    faqs: [
      { q: "Custom software or an existing product?", a: "If a SaaS handles your process without forcing it, use it. Custom software makes sense when your process is your competitive advantage or when per-user licenses already cost more than building." },
      { q: "Who owns the code?", a: "At DEVOLUZION, you do: code, documentation and infrastructure. Demand the same from any provider." },
    ],
  },
  {
    id: "automatizar-con-ia",
    slug: "what-business-processes-can-ai-automate",
    title: "What Business Processes Can AI Automate? (With Real Examples)",
    desc: "Processes AI can automate in your company today: sales, support, operations and back-office, with concrete examples.",
    minutes: 5,
    date: "2026-07-14",
    wa: "ai",
    ctaTitle: "Let's find out what to automate first in your business",
    sections: [
      {
        h: "Sales: never a cold lead again",
        p: ["Every lead gets a response in seconds — at any hour. Automatic qualification by your criteria, persistent follow-up via WhatsApp and email, and direct scheduling into your salespeople's calendars. The typical effect: more meetings from the same traffic."],
      },
      {
        h: "Customer support: 70% resolves itself",
        p: ["Order status, hours, prices, return policies: an agent trained on your information resolves most inquiries instantly and escalates the rest with full context."],
      },
      {
        h: "Operations and back-office",
        p: ["Classifying emails and documents, extracting data from invoices, generating daily reports, syncing spreadsheets across systems. Whatever your team does by 'copying and pasting' is exactly what AI automates best."],
      },
      {
        h: "Marketing: more iterations, better aim",
        p: ["Ad variants, assisted targeting, result summaries and alerts when a metric moves. AI doesn't replace strategy: it accelerates it."],
      },
      {
        h: "Where to start",
        p: ["The rule: automate first what's repetitive, frequent and rule-based. If a task is done the same way 20 times a week, it's a candidate. If it requires complex judgment every time, leave it to humans — for now."],
      },
    ],
  },
  {
    id: "web-vs-ecommerce",
    slug: "website-vs-ecommerce",
    title: "Website vs Ecommerce: What Does Your Business Actually Need?",
    desc: "The differences between a website and an ecommerce store, when each makes sense and how to evolve from one to the other.",
    minutes: 4,
    date: "2026-07-17",
    wa: "ecommerce",
    ctaTitle: "We'll help you decide with your business numbers",
    sections: [
      {
        h: "The essential difference",
        p: ["A website presents, persuades and generates contact. An ecommerce sells directly: catalog, cart, payment and shipping without human intervention. The first turns visits into conversations; the second, into transactions."],
      },
      {
        h: "When a website is enough",
        p: ["If you sell services, high-value quoted products, or your sales cycle requires conversation (B2B, projects, consulting), a great website with a strong form and WhatsApp outperforms a forced store."],
      },
      {
        h: "When you need ecommerce",
        p: ["If you sell repeat-purchase products, your catalog exceeds 20-30 items, or customers constantly ask about prices and stock, ecommerce stops being optional: every manual sale is lost margin."],
      },
      {
        h: "The smart path",
        p: ["Many companies start with a premium website + catalog without checkout (orders come through WhatsApp) and evolve to full checkout when volume justifies it. The right architecture lets you grow without rebuilding everything."],
      },
    ],
    faqs: [
      { q: "Can I start with a website and add ecommerce later?", a: "Yes, if the foundation is well-built. Our sites are developed with that evolution in mind: catalog, payments and automation can be added without rebuilding." },
      { q: "How much does each cost?", a: "Premium website from USD 149 (START plan); ecommerce from USD 499 (COMMERCE plan). Both include technical SEO and analytics." },
    ],
  },
  {
    id: "que-hace-agencia",
    slug: "what-does-a-digital-marketing-agency-do",
    title: "What Does a Digital Marketing Agency Do? (And How to Pick a Good One)",
    desc: "What a digital marketing agency actually does, which services it should offer and the signals of a serious one.",
    minutes: 5,
    date: "2026-07-20",
    wa: "growth",
    ctaTitle: "Talk to DEVOLUZION about your growth",
    sections: [
      {
        h: "What it should do",
        p: ["A serious agency does four things: strategy (who you're selling to and why they choose you), execution (campaigns, content, SEO), measurement (what came back for every dollar spent) and optimization (improving next month with what was learned). Everything else is accessory."],
      },
      {
        h: "Typical services, explained",
        p: ["Google Ads to capture existing demand, Meta Ads to generate new demand, SEO to stop paying for every click forever, email/WhatsApp marketing to sell to people who already know you, and analytics to know what worked."],
      },
      {
        h: "Signals of a good agency",
        p: ["It talks about sales and cost per customer, not likes. It shows you live dashboards, not pretty PDFs. It tells you what it WOULDN'T do in your case. And the ad account stays in your name."],
        list: ["Reports leads and sales, not impressions", "The ad account is yours", "Proposes before promising", "Explains without jargon"],
      },
      {
        h: "Red flags",
        p: ["Promises of first place on Google in two weeks, reports you can't verify, long contracts without minimum results and the phrase 'trust us' as an answer to questions about numbers."],
      },
    ],
  },
  {
    id: "seo-vs-ads",
    slug: "seo-vs-google-ads",
    title: "SEO vs Google Ads: Where Should You Invest First?",
    desc: "SEO or Google Ads: differences, costs, time to results and how to combine them to maximize return.",
    minutes: 5,
    date: "2026-07-23",
    wa: "growth",
    ctaTitle: "We'll honestly tell you what fits your case",
    sections: [
      {
        h: "The difference in one sentence",
        p: ["Google Ads buys immediate visibility; SEO builds visibility you don't pay for per click. One is renting, the other is owning."],
      },
      {
        h: "When Google Ads wins",
        p: ["When you need results this month, when you're validating a new market, or in industries where the first organic result takes years. Ads also teach you which keywords convert — information your SEO then uses."],
      },
      {
        h: "When SEO wins",
        p: ["When your customer researches before buying (most cases), when your industry's cost per click is high, and when you think in years, not weeks. Organic traffic compounds: each month adds to the last."],
      },
      {
        h: "The right answer: both, in order",
        p: ["The best-performing strategy: Ads from day one to generate flow and learn what converts; SEO in parallel so that in 6-12 months a growing share of your traffic stops costing per click. Budget shifts with the data."],
      },
    ],
    faqs: [
      { q: "How much should I invest in each?", a: "It depends on your industry's CPC and your capacity to handle leads. In a first conversation we estimate both scenarios with data from your vertical." },
      { q: "Does SEO work for small businesses?", a: "Yes, especially local SEO and niche keywords. Small businesses win where the big ones don't bother competing." },
    ],
  },
  {
    id: "automatizar-whatsapp",
    slug: "how-to-automate-whatsapp-for-business",
    title: "How to Automate WhatsApp for Business (Without Getting Banned)",
    desc: "A guide to automating WhatsApp Business with the official API: replies, follow-up and automated sales with zero ban risk.",
    minutes: 5,
    date: "2026-07-26",
    wa: "automation",
    ctaTitle: "Automate your WhatsApp with DEVOLUZION",
    sections: [
      {
        h: "Why WhatsApp is THE channel in many markets",
        p: ["Your customers don't check email: they check WhatsApp. Open rates exceed 90% and replies are nearly instant. The problem: handling it by hand doesn't scale, and leaving it unanswered kills sales."],
      },
      {
        h: "The golden rule: official API or nothing",
        p: ["There are pirate tools that automate WhatsApp Web. They work... until your number gets permanently banned. The right path is the official WhatsApp Business API: approved templates, customer opt-in and zero risk of losing your number."],
      },
      {
        h: "What to automate first",
        p: ["The first reply (nobody waits more than 5 minutes), contact classification (sales, support, info), scheduling (straight into your calendar) and follow-up (quotes that never go cold). With those four, 80% of the value is captured."],
        list: ["Instant 24/7 replies", "Automatic classification and routing", "Scheduling with reminders", "Quote follow-up", "CRM logging"],
      },
      {
        h: "Humans still matter",
        p: ["Automation handles the repetitive and escalates the complex with context. The customer notices speed and order, not a robot. That balance — instant but human when it counts — is what converts."],
      },
    ],
  },
  {
    id: "ia-aumentar-ventas",
    slug: "how-to-use-ai-to-increase-sales",
    title: "How Businesses Can Use AI to Increase Sales (No Hype)",
    desc: "Concrete ways AI increases sales: response speed, follow-up, personalization and demand prediction.",
    minutes: 5,
    date: "2026-07-29",
    wa: "ai",
    ctaTitle: "Let's talk about AI applied to your sales",
    sections: [
      {
        h: "Response speed = sales",
        p: ["The data nobody wants to hear: a lead that waits more than an hour barely converts. AI responds in seconds, at any hour, every day. That alone — changing nothing else — usually moves the number of booked meetings."],
      },
      {
        h: "Follow-up no human can sustain",
        p: ["80% of sales require 5+ touches; most salespeople give up after the second. An AI sequence follows up persistently and elegantly via WhatsApp and email until the lead says yes, says no, or books."],
      },
      {
        h: "Personalization at scale",
        p: ["AI adapts messages, offers and content based on what it knows about each customer: what they viewed, bought or asked. What used to be impossible outside of Amazon is now within reach of an SMB."],
      },
      {
        h: "Predict to sell better",
        p: ["With your historical data, AI estimates which customers are about to buy, which are about to leave and which products to push each month. Selling stops being intuition and becomes informed prioritization."],
      },
      {
        h: "Where to start this week",
        p: ["Simple question: how many leads went unanswered last month? That number, times your average ticket, is the cost of not starting. The first step is a sales agent that responds, qualifies and books."],
      },
    ],
  },
];
